/**
 * Browse Store
 * Manages zones, vendors, and discovery state
 */

import { create } from 'zustand';
import { Zone, Vendor } from '@/lib/constants';
import { zonesApi, vendorsApi } from '@/lib/api';
import { logger } from '@/lib/logger';

interface BrowseState {
  // Selected zone
  selectedZone: Zone | null;
  zones: Zone[];
  
  // Vendors
  vendors: Vendor[];
  selectedVendor: Vendor | null;
  
  // Menu items from selected vendor
  menuItems: any[];
  
  // UI state
  isLoadingZones: boolean;
  isLoadingVendors: boolean;
  isLoadingMenu: boolean;
  error: string | null;
  
  // Search & filters
  searchQuery: string;
  categoryFilter: string | null;
  openNowFilter: boolean;
  sortBy: 'rating' | 'distance' | 'delivery_time';
  
  // Actions
  fetchZones: () => Promise<void>;
  selectZone: (zone: Zone) => void;
  fetchVendors: () => Promise<void>;
  searchVendors: (query: string) => void;
  setFilters: (filters: { category?: string | null; openNow?: boolean; sortBy?: 'rating' | 'distance' | 'delivery_time' }) => void;
  selectVendor: (vendorId: string) => Promise<void>;
  fetchMenu: (vendorId: string) => Promise<void>;
  clearError: () => void;
}

export const useBrowseStore = create<BrowseState>((set, get) => ({
  selectedZone: null,
  zones: [],
  vendors: [],
  selectedVendor: null,
  menuItems: [],
  isLoadingZones: false,
  isLoadingVendors: false,
  isLoadingMenu: false,
  error: null,
  searchQuery: '',
  categoryFilter: null,
  openNowFilter: false,
  sortBy: 'rating',

  fetchZones: async () => {
    set({ isLoadingZones: true, error: null });
    try {
      const zones = await zonesApi.getZones();
      set({ zones, isLoadingZones: false });
      logger.info('Zones loaded', { count: zones.length });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load zones';
      set({ isLoadingZones: false, error: errorMessage });
      logger.error('Failed to fetch zones', error as Error);
    }
  },

  selectZone: (zone: Zone) => {
    set({ selectedZone: zone });
    logger.info('Zone selected', { zoneId: zone.id, zoneName: zone.name });
    
    // Auto-fetch vendors for this zone
    get().fetchVendors();
  },

  fetchVendors: async () => {
    const { selectedZone, searchQuery, categoryFilter, openNowFilter, sortBy } = get();
    
    if (!selectedZone) {
      set({ error: 'Please select a zone first' });
      return;
    }

    set({ isLoadingVendors: true, error: null });
    try {
      const vendors = await vendorsApi.search({
        zone: selectedZone.id,
        q: searchQuery || undefined,
        category: categoryFilter || undefined,
        open_now: openNowFilter,
        sort: sortBy,
      });
      set({ vendors, isLoadingVendors: false });
      logger.info('Vendors loaded', { count: vendors.length });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load vendors';
      set({ isLoadingVendors: false, error: errorMessage });
      logger.error('Failed to fetch vendors', error as Error);
    }
  },

  searchVendors: (query: string) => {
    set({ searchQuery: query });
    get().fetchVendors();
  },

  setFilters: (filters) => {
    set({
      categoryFilter: filters.category !== undefined ? filters.category : get().categoryFilter,
      openNowFilter: filters.openNow !== undefined ? filters.openNow : get().openNowFilter,
      sortBy: filters.sortBy !== undefined ? filters.sortBy : get().sortBy,
    });
    get().fetchVendors();
  },

  selectVendor: async (vendorId: string) => {
    set({ isLoadingMenu: true, error: null });
    try {
      const vendor = await vendorsApi.getVendor(vendorId);
      set({ selectedVendor: vendor, isLoadingMenu: false });
      logger.info('Vendor selected', { vendorId: vendor.id });
      
      // Auto-fetch menu
      await get().fetchMenu(vendorId);
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load vendor';
      set({ isLoadingMenu: false, error: errorMessage });
      logger.error('Failed to fetch vendor', error as Error);
    }
  },

  fetchMenu: async (vendorId: string) => {
    set({ isLoadingMenu: true, error: null });
    try {
      const menuItems = await vendorsApi.getMenu(vendorId);
      set({ menuItems, isLoadingMenu: false });
      logger.info('Menu loaded', { vendorId, itemCount: menuItems.length });
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to load menu';
      set({ isLoadingMenu: false, error: errorMessage });
      logger.error('Failed to fetch menu', error as Error);
    }
  },

  clearError: () => set({ error: null }),
}));

















